import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { getGeometryStats } from '../utils/geometry';

const Viewer3D = forwardRef(({
  bgColor = '#0b1020',
  theme = 'light',
  showGrid = true,
  showAxes = false,
  wireframe = false,
  buildPlateSize = 200,
  showBuildPlate = true,
  transformMode = 'translate',
  onStatsUpdate,
  onError,
  onObjectSelected
}, ref) => {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const controlsRef = useRef(null);
  const transformControlsRef = useRef(null);
  const modelGroupRef = useRef(null);
  const gridRef = useRef(null);
  const axesRef = useRef(null);
  const buildPlateRef = useRef(null);
  const measurementGroupRef = useRef(null);
  const animationIdRef = useRef(null);
  const selectedObjectRef = useRef(null);
  const callbacksRef = useRef({ onStatsUpdate, onError, onObjectSelected });

  useEffect(() => {
    callbacksRef.current = { onStatsUpdate, onError, onObjectSelected };
  }, [onStatsUpdate, onError, onObjectSelected]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    addObject: (object) => {
      if (!modelGroupRef.current) return;

      // Center horizontally, then place the bottom on the build plate.
      const box = new THREE.Box3().setFromObject(object);
      const center = box.getCenter(new THREE.Vector3());
      object.position.sub(center);
      object.updateMatrixWorld(true);

      const groundedBox = new THREE.Box3().setFromObject(object);
      object.position.y -= groundedBox.min.y;

      // Make object selectable
      object.userData.selectable = true;
      object.name = object.name || object.userData.name || 'Model';

      modelGroupRef.current.add(object);
      updateStats();
      selectObject(object);
      fitCameraToObject(modelGroupRef.current);
    },

    clearScene: () => {
      if (!modelGroupRef.current) return;

      deselectObject();

      while (modelGroupRef.current.children.length > 0) {
        const child = modelGroupRef.current.children[0];
        disposeObject(child);
        modelGroupRef.current.remove(child);
      }

      updateStats();
    },

    getScene: () => sceneRef.current,
    getModelGroup: () => modelGroupRef.current,
    getRenderer: () => rendererRef.current,
    getCamera: () => cameraRef.current,
    getSelectedObject: () => selectedObjectRef.current,
    getSelectedObjectInfo: () => getObjectInfo(selectedObjectRef.current),

    placeOnPlatform: () => {
      const selected = selectedObjectRef.current;
      if (!selected) return;

      const box = new THREE.Box3().setFromObject(selected);
      selected.position.y -= box.min.y;
      selected.updateMatrixWorld(true);
      updateStats();
      updateMeasurementOverlay();
      emitSelection();
    },

    duplicateSelected: () => {
      const selected = selectedObjectRef.current;
      if (!selected || !modelGroupRef.current) return;

      const clone = selected.clone(true);
      clone.traverse((child) => {
        child.userData = { ...child.userData, selectable: true };
        if (child.isMesh) {
          child.geometry = child.geometry?.clone();
          if (Array.isArray(child.material)) {
            child.material = child.material.map((material) => material.clone());
          } else if (child.material) {
            child.material = child.material.clone();
          }
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });

      const box = new THREE.Box3().setFromObject(selected);
      const size = box.getSize(new THREE.Vector3());
      clone.position.x += Math.max(8, size.x * 0.35);
      clone.position.z += Math.max(8, size.z * 0.35);
      clone.name = `${selected.name || 'Model'} copy`;

      modelGroupRef.current.add(clone);
      selectObject(clone);
      updateStats();
    },

    deleteSelected: () => {
      const selected = selectedObjectRef.current;
      if (!selected || !modelGroupRef.current) return;

      deselectObject();
      disposeObject(selected);
      modelGroupRef.current.remove(selected);
      updateStats();
    },

    updateSelectedTransform: (patch = {}) => {
      const selected = selectedObjectRef.current;
      if (!selected) return;

      if (patch.position) {
        ['x', 'y', 'z'].forEach((axis) => {
          const value = Number(patch.position[axis]);
          if (Number.isFinite(value)) selected.position[axis] = value;
        });
      }

      if (patch.rotation) {
        ['x', 'y', 'z'].forEach((axis) => {
          const value = Number(patch.rotation[axis]);
          if (Number.isFinite(value)) {
            selected.rotation[axis] = THREE.MathUtils.degToRad(value);
          }
        });
      }

      if (patch.scale) {
        ['x', 'y', 'z'].forEach((axis) => {
          const value = Number(patch.scale[axis]);
          if (Number.isFinite(value)) selected.scale[axis] = Math.max(0.001, value);
        });
      }

      selected.updateMatrixWorld(true);
      updateStats();
      updateMeasurementOverlay();
      emitSelection();
    },

    setSelectedDimensions: (targetDimensions = {}, lockAspect = false) => {
      const selected = selectedObjectRef.current;
      if (!selected) return;

      selected.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(selected);
      const currentSize = box.getSize(new THREE.Vector3());
      const axes = ['x', 'y', 'z'];
      const ratios = axes
        .map((axis) => {
          const target = Number(targetDimensions[axis]);
          const current = currentSize[axis];
          if (!Number.isFinite(target) || target <= 0 || !Number.isFinite(current) || current <= 0) {
            return null;
          }

          const ratio = target / current;
          return Number.isFinite(ratio) && ratio > 0 ? { axis, ratio } : null;
        })
        .filter(Boolean);

      if (ratios.length === 0) return;

      if (lockAspect) {
        const changedRatio = ratios.reduce((best, item) => {
          const delta = Math.abs(Math.log(item.ratio || 1));
          const bestDelta = Math.abs(Math.log(best.ratio || 1));
          return delta > bestDelta ? item : best;
        }, ratios[0]);
        selected.scale.multiplyScalar(changedRatio.ratio);
      } else {
        axes.forEach((axis) => {
          const target = Number(targetDimensions[axis]);
          const current = currentSize[axis];
          if (Number.isFinite(target) && target > 0 && current > 0) {
            selected.scale[axis] *= target / current;
          }
        });
      }

      selected.updateMatrixWorld(true);
      updateStats();
      updateMeasurementOverlay();
      fitCameraToObject(selected);
      emitSelection();
    },

    resetCamera: () => {
      if (modelGroupRef.current && modelGroupRef.current.children.length > 0) {
        fitCameraToObject(modelGroupRef.current);
      } else if (cameraRef.current && controlsRef.current) {
        cameraRef.current.position.set(buildPlateSize * 1.35, buildPlateSize, buildPlateSize * 1.35);
        controlsRef.current.target.set(0, 0, 0);
        controlsRef.current.update();
      }
    },

    frameSelected: () => {
      if (selectedObjectRef.current) {
        fitCameraToObject(selectedObjectRef.current);
      } else if (modelGroupRef.current && modelGroupRef.current.children.length > 0) {
        fitCameraToObject(modelGroupRef.current);
      }
    },

    takeScreenshot: (filename = 'screenshot.png') => {
      if (!rendererRef.current) return;
      const url = rendererRef.current.domElement.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
    }
  }));

  const disposeObject = (object) => {
    object.traverse((child) => {
      if (child.geometry) {
        child.geometry?.dispose();
      }
      if (child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach(m => m.dispose());
        } else {
          child.material?.dispose();
        }
      }
    });
  };

  const updateStats = () => {
    if (!modelGroupRef.current || !callbacksRef.current.onStatsUpdate) return;
    
    try {
      const stats = getGeometryStats(modelGroupRef.current);
      callbacksRef.current.onStatsUpdate(stats);
    } catch (error) {
      console.error('Stats update error:', error);
    }
  };

  const formatMeasurement = (value) => {
    if (!Number.isFinite(value)) return '0 mm';
    const rounded = value >= 100 ? value.toFixed(0) : value.toFixed(1);
    return `${Number(rounded).toString()} mm`;
  };

  const makeLine = (points, color = 0x0f7fd1) => {
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineBasicMaterial({
      color,
      depthTest: false,
      transparent: true,
      opacity: 0.92
    });
    const line = new THREE.Line(geometry, material);
    line.renderOrder = 20;
    return line;
  };

  const makeTextSprite = (text, color = '#0f7fd1') => {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 192;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'rgba(248, 250, 252, 0.94)';
    ctx.strokeStyle = color;
    ctx.lineWidth = 10;
    ctx.beginPath();
    ctx.roundRect(20, 36, 472, 120, 28);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#132033';
    ctx.font = '700 58px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, canvas.width / 2, canvas.height / 2 + 4);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const material = new THREE.SpriteMaterial({
      map: texture,
      depthTest: false,
      transparent: true
    });
    const sprite = new THREE.Sprite(material);
    sprite.renderOrder = 30;
    return sprite;
  };

  const disposeMeasurementGroup = () => {
    const group = measurementGroupRef.current;
    if (!group || !sceneRef.current) return;

    sceneRef.current.remove(group);
    group.traverse((child) => {
      child.geometry?.dispose();
      if (child.material) {
        if (child.material.map) child.material.map.dispose();
        child.material.dispose();
      }
    });
    measurementGroupRef.current = null;
  };

  const updateMeasurementOverlay = () => {
    disposeMeasurementGroup();

    const selected = selectedObjectRef.current;
    const scene = sceneRef.current;
    if (!selected || !scene) return;

    selected.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(selected);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    if (!Number.isFinite(maxDim) || maxDim <= 0) return;

    const min = box.min;
    const max = box.max;
    const offset = Math.max(7, maxDim * 0.2);
    const tick = Math.max(2.5, maxDim * 0.05);
    const group = new THREE.Group();
    group.name = 'Cadify measurement overlay';

    const boxHelper = new THREE.Box3Helper(box, 0x0f7fd1);
    boxHelper.material.depthTest = false;
    boxHelper.material.transparent = true;
    boxHelper.material.opacity = 0.7;
    boxHelper.renderOrder = 18;
    group.add(boxHelper);

    const xStart = new THREE.Vector3(min.x, min.y, max.z + offset);
    const xEnd = new THREE.Vector3(max.x, min.y, max.z + offset);
    const zStart = new THREE.Vector3(max.x + offset, min.y, min.z);
    const zEnd = new THREE.Vector3(max.x + offset, min.y, max.z);
    const yStart = new THREE.Vector3(max.x + offset, min.y, max.z + offset);
    const yEnd = new THREE.Vector3(max.x + offset, max.y, max.z + offset);

    group.add(makeLine([xStart, xEnd], 0x0f7fd1));
    group.add(makeLine([zStart, zEnd], 0x14a37f));
    group.add(makeLine([yStart, yEnd], 0xf59e0b));

    [
      [xStart, new THREE.Vector3(xStart.x, xStart.y + tick, xStart.z)],
      [xEnd, new THREE.Vector3(xEnd.x, xEnd.y + tick, xEnd.z)],
      [zStart, new THREE.Vector3(zStart.x, zStart.y + tick, zStart.z)],
      [zEnd, new THREE.Vector3(zEnd.x, zEnd.y + tick, zEnd.z)],
      [yStart, new THREE.Vector3(yStart.x + tick, yStart.y, yStart.z)],
      [yEnd, new THREE.Vector3(yEnd.x + tick, yEnd.y, yEnd.z)]
    ].forEach(([start, end]) => {
      group.add(makeLine([start, end], 0x64748b));
    });

    const labelScale = Math.max(4.8, maxDim * 0.08);
    const xLabel = makeTextSprite(formatMeasurement(size.x), '#0f7fd1');
    xLabel.position.copy(xStart.clone().lerp(xEnd, 0.5)).add(new THREE.Vector3(0, tick * 1.6, offset * 0.15));
    xLabel.scale.set(labelScale * 2.6, labelScale, 1);
    group.add(xLabel);

    const zLabel = makeTextSprite(formatMeasurement(size.z), '#14a37f');
    zLabel.position.copy(zStart.clone().lerp(zEnd, 0.5)).add(new THREE.Vector3(tick * 1.7, tick * 1.2, 0));
    zLabel.scale.set(labelScale * 2.6, labelScale, 1);
    group.add(zLabel);

    const yLabel = makeTextSprite(formatMeasurement(size.y), '#f59e0b');
    yLabel.position.copy(yStart.clone().lerp(yEnd, 0.5)).add(new THREE.Vector3(tick * 1.8, 0, offset * 0.15));
    yLabel.scale.set(labelScale * 2.6, labelScale, 1);
    group.add(yLabel);

    scene.add(group);
    measurementGroupRef.current = group;
  };

  const getObjectInfo = (object) => {
    if (!object) return null;

    object.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(object);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());

    return {
      id: object.uuid,
      name: object.name || object.userData?.name || 'Model',
      type: object.userData?.type || object.type || 'Object',
      dimensions: {
        x: size.x,
        y: size.y,
        z: size.z
      },
      center: {
        x: center.x,
        y: center.y,
        z: center.z
      },
      position: {
        x: object.position.x,
        y: object.position.y,
        z: object.position.z
      },
      rotation: {
        x: THREE.MathUtils.radToDeg(object.rotation.x),
        y: THREE.MathUtils.radToDeg(object.rotation.y),
        z: THREE.MathUtils.radToDeg(object.rotation.z)
      },
      scale: {
        x: object.scale.x,
        y: object.scale.y,
        z: object.scale.z
      }
    };
  };

  const emitSelection = () => {
    const selected = selectedObjectRef.current;
    const info = getObjectInfo(selected);
    if (callbacksRef.current.onObjectSelected) {
      callbacksRef.current.onObjectSelected(selected, info);
    }
  };

  const selectObject = (object) => {
    if (!object || !transformControlsRef.current) return;

    selectedObjectRef.current = object;
    transformControlsRef.current.attach(object);
    updateMeasurementOverlay();
    emitSelection();
  };

  const deselectObject = () => {
    if (transformControlsRef.current) {
      transformControlsRef.current.detach();
    }
    selectedObjectRef.current = null;
    disposeMeasurementGroup();
    emitSelection();
  };

  const fitCameraToObject = (object, offset = 2.45) => {
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    if (!camera || !controls || !object) return;

    const box = new THREE.Box3().setFromObject(object);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());

    const maxSize = Math.max(size.x, size.y, size.z);
    if (!Number.isFinite(maxSize) || maxSize <= 0) return;

    const fov = THREE.MathUtils.degToRad(camera.fov);
    const distance = (maxSize / 2) / Math.tan(fov / 2);

    const dir = new THREE.Vector3(1, 0.8, 1).normalize();
    camera.position.copy(center.clone().add(dir.multiplyScalar(distance * offset)));

    camera.near = Math.max(0.01, distance / 1000);
    camera.far = distance * 1000 + maxSize * 2;
    camera.updateProjectionMatrix();

    controls.target.copy(center);
    controls.update();

    // Update grid size
    if (gridRef.current) {
      const gridSize = Math.max(10, Math.ceil(maxSize * 2));
      gridRef.current.scale.setScalar(gridSize / 10);
    }
  };

  // Create build plate
  const createBuildPlate = (size) => {
    const group = new THREE.Group();
    const dark = theme === 'dark';

    // Platform surface
    const platformGeometry = new THREE.BoxGeometry(size, 0.5, size);
    const platformMaterial = new THREE.MeshStandardMaterial({
      color: dark ? 0x182235 : 0xe7edf4,
      metalness: dark ? 0.1 : 0.05,
      roughness: 0.82
    });
    const platform = new THREE.Mesh(platformGeometry, platformMaterial);
    platform.position.y = -0.25;
    platform.receiveShadow = true;
    group.add(platform);

    // Border lines
    const borderGeometry = new THREE.EdgesGeometry(new THREE.BoxGeometry(size, 0.01, size));
    const borderMaterial = new THREE.LineBasicMaterial({ color: dark ? 0x2dd4bf : 0x14966f, linewidth: 2 });
    const border = new THREE.LineSegments(borderGeometry, borderMaterial);
    border.position.y = 0.01;
    group.add(border);

    // Grid on platform
    const gridHelper = new THREE.GridHelper(
      size,
      size / 10,
      dark ? 0x5f718a : 0x93a4b8,
      dark ? 0x28364c : 0xd0d8e4
    );
    gridHelper.position.y = 0.01;
    group.add(gridHelper);

    return group;
  };

  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(bgColor);

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      preserveDrawingBuffer: true
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    // Camera setup
    const camera = new THREE.PerspectiveCamera(
      60,
      container.clientWidth / container.clientHeight,
      0.01,
      100000
    );
    camera.position.set(buildPlateSize * 1.5, buildPlateSize, buildPlateSize * 1.5);

    // Controls setup
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.screenSpacePanning = false;
    controls.minDistance = 0.1;
    controls.maxDistance = 10000;
    controls.target.set(0, 0, 0);

    // Lighting
    const ambient = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambient);

    const dir1 = new THREE.DirectionalLight(0xffffff, 1.0);
    dir1.position.set(buildPlateSize, buildPlateSize * 2, buildPlateSize);
    dir1.castShadow = true;
    dir1.shadow.mapSize.width = 2048;
    dir1.shadow.mapSize.height = 2048;
    dir1.shadow.camera.left = -buildPlateSize;
    dir1.shadow.camera.right = buildPlateSize;
    dir1.shadow.camera.top = buildPlateSize;
    dir1.shadow.camera.bottom = -buildPlateSize;
    scene.add(dir1);

    const dir2 = new THREE.DirectionalLight(0xffffff, 0.6);
    dir2.position.set(-buildPlateSize, buildPlateSize, -buildPlateSize);
    scene.add(dir2);

    // Build plate
    const buildPlate = createBuildPlate(buildPlateSize);
    buildPlate.visible = showBuildPlate;
    scene.add(buildPlate);

    // Grid (separate from build plate)
    const grid = new THREE.GridHelper(buildPlateSize * 2, buildPlateSize * 2 / 10, 0x444444, 0x222222);
    grid.visible = showGrid && !showBuildPlate;
    grid.position.y = -0.01;
    scene.add(grid);

    // Axes
    const axes = new THREE.AxesHelper(buildPlateSize / 2);
    axes.visible = showAxes;
    scene.add(axes);

    // Model group
    const modelGroup = new THREE.Group();
    scene.add(modelGroup);

    // Transform controls
    const transformControls = new TransformControls(camera, renderer.domElement);
    transformControls.setMode(transformMode);
    transformControls.setSize(0.8);
    scene.add(transformControls);

    // Disable orbit controls when using transform controls
    transformControls.addEventListener('dragging-changed', (event) => {
      controls.enabled = !event.value;
    });
    transformControls.addEventListener('objectChange', () => {
      updateStats();
      updateMeasurementOverlay();
      emitSelection();
    });

    container.appendChild(renderer.domElement);

    // Store refs
    sceneRef.current = scene;
    cameraRef.current = camera;
    rendererRef.current = renderer;
    controlsRef.current = controls;
    transformControlsRef.current = transformControls;
    modelGroupRef.current = modelGroup;
    gridRef.current = grid;
    axesRef.current = axes;
    buildPlateRef.current = buildPlate;

    // Resize handler
    const onResize = () => {
      if (!container || !renderer || !camera) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w <= 0 || h <= 0) return;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', onResize);
    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(container);

    // Object selection
    const raycaster = new THREE.Raycaster();
    const onClick = (e) => {
      // Ignore if clicking on transform controls
      if (e.target !== renderer.domElement) return;

      const rect = renderer.domElement.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera({ x, y }, camera);

      const hits = raycaster.intersectObjects(modelGroup.children, true);

      if (hits.length > 0) {
        // Find the top-level object
        let selectedObj = hits[0].object;
        while (selectedObj.parent && selectedObj.parent !== modelGroup) {
          selectedObj = selectedObj.parent;
        }

        if (selectedObj.userData.selectable) {
          selectObject(selectedObj);
        }
      } else {
        // Clicked on empty space - deselect
        deselectObject();
      }
    };
    renderer.domElement.addEventListener('click', onClick);

    // Double-click to focus
    const onDblClick = (e) => {
      const rect = renderer.domElement.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera({ x, y }, camera);
      const hits = raycaster.intersectObjects(modelGroup.children, true);
      if (hits[0]) {
        controls.target.copy(hits[0].point);
        controls.update();
      }
    };
    renderer.domElement.addEventListener('dblclick', onDblClick);

    // Animation loop
    const animate = () => {
      controls.update();
      renderer.render(scene, camera);
      animationIdRef.current = requestAnimationFrame(animate);
    };
    animate();

    // Cleanup
    return () => {
      cancelAnimationFrame(animationIdRef.current);
      window.removeEventListener('resize', onResize);
      resizeObserver.disconnect();
      renderer.domElement.removeEventListener('click', onClick);
      renderer.domElement.removeEventListener('dblclick', onDblClick);
      transformControls.dispose();
      controls.dispose();
      disposeMeasurementGroup();
      renderer.dispose();
      container.removeChild(renderer.domElement);

      modelGroup.traverse((obj) => {
        if (obj.isMesh) {
          obj.geometry?.dispose();
          if (Array.isArray(obj.material)) {
            obj.material.forEach(m => m.dispose());
          } else {
            obj.material?.dispose();
          }
        }
      });
    };
  }, []);

  // Update build plate and helper scale without recreating the whole scene.
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    if (buildPlateRef.current) {
      scene.remove(buildPlateRef.current);
      disposeObject(buildPlateRef.current);
    }

    const buildPlate = createBuildPlate(buildPlateSize);
    buildPlate.visible = showBuildPlate;
    scene.add(buildPlate);
    buildPlateRef.current = buildPlate;

    if (gridRef.current) {
      scene.remove(gridRef.current);
      disposeObject(gridRef.current);
    }

    const grid = new THREE.GridHelper(
      buildPlateSize * 2,
      Math.max(4, Math.round((buildPlateSize * 2) / 10)),
      theme === 'dark' ? 0x5f718a : 0x7c8aa5,
      theme === 'dark' ? 0x28364c : 0xd4dce8
    );
    grid.visible = showGrid && !showBuildPlate;
    grid.position.y = -0.01;
    scene.add(grid);
    gridRef.current = grid;

    if (axesRef.current) {
      axesRef.current.scale.setScalar(buildPlateSize / 200);
    }
  }, [buildPlateSize, theme]);

  // Update background color
  useEffect(() => {
    if (sceneRef.current) {
      sceneRef.current.background = new THREE.Color(bgColor);
    }
  }, [bgColor]);

  // Update grid visibility
  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.visible = showGrid && !showBuildPlate;
    }
  }, [showGrid, showBuildPlate]);

  // Update axes visibility
  useEffect(() => {
    if (axesRef.current) {
      axesRef.current.visible = showAxes;
    }
  }, [showAxes]);

  // Update build plate visibility
  useEffect(() => {
    if (buildPlateRef.current) {
      buildPlateRef.current.visible = showBuildPlate;
    }
  }, [showBuildPlate]);

  // Update wireframe
  useEffect(() => {
    if (!modelGroupRef.current) return;

    modelGroupRef.current.traverse((obj) => {
      if (obj.isMesh) {
        const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
        materials.forEach(m => {
          if (m) m.wireframe = wireframe;
        });
      }
    });
  }, [wireframe]);

  // Update transform mode
  useEffect(() => {
    if (transformControlsRef.current) {
      transformControlsRef.current.setMode(transformMode);
    }
  }, [transformMode]);

  return (
    <div 
      ref={mountRef} 
      className="w-full h-full"
      style={{ cursor: 'grab' }}
    />
  );
});

Viewer3D.displayName = 'Viewer3D';

export default Viewer3D;

